import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check, X } from 'lucide-react';

const MultiSelectDropdown = ({ 
  label, 
  options = [], 
  selectedValues = [], 
  onChange, 
  placeholder = "Select options...",
  className = "",
  maxHeight = "200px",
  showSelectedCount = true,
  isSearchable = false,
  searchPlaceholder = "Search...",
  showSerialNumber = false,
  hideSelectedChips = false,
  dark = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && isSearchable && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 0);
    }
    
    // Reset search term when dropdown closes
    if (!isOpen) {
      setSearchTerm("");
    }
  }, [isOpen, isSearchable]);

  const handleToggleOption = (value) => {
    const newSelectedValues = selectedValues.includes(value)
      ? selectedValues.filter(v => v !== value)
      : [...selectedValues, value];
    
    onChange(newSelectedValues);
  };

  const handleClearAll = () => {
    onChange([]);
  };

  const getDisplayText = () => {
    if (selectedValues.length === 0) {
      return placeholder;
    }
    
    if (showSelectedCount && selectedValues.length > 1) {
      return `${selectedValues.length} selected`;
    }
    
    const firstValue = selectedValues[0];
    const option = options.find(opt => (typeof opt === 'object' ? opt.value : opt) === firstValue);
    return typeof option === 'object' ? option.label : firstValue;
  };
  
  const filteredOptions = options.filter(option => {
    if (!isSearchable || !searchTerm) return true;
    const label = typeof option === 'object' ? option.label : option;
    return String(label).toLowerCase().includes(searchTerm.toLowerCase());
  });

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
        </label>
      )}
      
      {/* Dropdown Trigger */}
      <div
        className={`min-h-[40px] px-3 py-2 border rounded-md cursor-pointer flex items-center justify-between focus:outline-none ${dark ? 'bg-[rgba(7,10,28,0.6)] border-[rgba(150,165,210,0.2)] rounded-[11px] hover:border-[#a855f7]/50 focus:ring-2 focus:ring-[rgba(168,85,247,0.2)]' : 'bg-white border-gray-300 hover:border-gray-400 focus:ring-2 focus:ring-blue-500'}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={`flex-1 truncate ${selectedValues.length === 0 ? (dark ? 'text-[#4a5378]' : 'text-gray-500') : (dark ? 'text-white' : 'text-gray-900')}`} title={getDisplayText()}>
          {getDisplayText()}
        </span>
        <div className="flex items-center space-x-2">
          {selectedValues.length > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleClearAll();
              }}
              className="text-gray-400 hover:text-gray-600 p-1"
            >
              <X className="h-3 w-3" />
            </button>
          )}
          <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${isOpen ? 'transform rotate-180' : ''}`} />
        </div>
      </div>

      {/* Selected Values Display */}
      {!hideSelectedChips && selectedValues.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {selectedValues.slice(0, 5).map((value, index) => {
             const option = options.find(opt => (typeof opt === 'object' ? opt.value : opt) === value);
             const label = typeof option === 'object' ? option.label : value;
             return (
              <span
                key={index}
                className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-md max-w-[150px]"
              >
                {showSerialNumber && (
                  <span className="inline-flex items-center justify-center bg-blue-200 text-blue-900 font-bold rounded-full w-4 h-4 mr-1 text-[10px] shrink-0">
                    {index + 1}
                  </span>
                )}
                <span className="truncate">{label}</span>
                <button
                  onClick={() => handleToggleOption(value)}
                  className="ml-1 hover:text-blue-600 shrink-0"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            );
          })}
          {selectedValues.length > 5 && (
            <span className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-md">
              +{selectedValues.length - 5} more
            </span>
          )}
        </div>
      )}

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className={`absolute z-[1300] mt-1 w-full rounded-md shadow-lg ${dark ? 'bg-[#0d1128] border border-[rgba(168,133,255,0.2)]' : 'bg-white border border-gray-300'}`}
          style={{ maxHeight: maxHeight, display: 'flex', flexDirection: 'column' }}
        >
          {isSearchable && (
            <div className={`p-2 border-b ${dark ? 'border-[rgba(150,165,210,0.14)]' : 'border-gray-200'}`}>
              <div className="relative">
                <input
                  ref={searchInputRef}
                  type="text"
                  className={`w-full px-3 py-1.5 text-sm rounded focus:outline-none focus:ring-1 ${dark ? 'bg-[rgba(7,10,28,0.6)] border border-[rgba(150,165,210,0.2)] text-white placeholder:text-[#4a5378] focus:ring-[rgba(168,85,247,0.3)]' : 'border border-gray-300 focus:ring-blue-500'}`}
                  placeholder={searchPlaceholder}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
                {searchTerm && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSearchTerm("");
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
            </div>
          )}
          <div className={`overflow-y-auto flex-1 ${dark ? 'no-scrollbar' : ''}`}>
            {filteredOptions.length === 0 ? (
              <div className={`px-3 py-2 text-sm ${dark ? 'text-[#8a92b0]' : 'text-gray-500'}`}>
                {searchTerm ? "No results found" : "No options available"}
              </div>
            ) : (
              filteredOptions.map((option, index) => {
                const isObject = typeof option === 'object' && option !== null;
                const optionValue = isObject ? option.value : option;
                const optionLabel = isObject ? option.label : option;
                const isSelected = selectedValues.includes(optionValue);
                return (
                  <div
                    key={index}
                    className={`px-3 py-2 cursor-pointer flex items-center justify-between ${dark ? (isSelected ? 'bg-[rgba(168,85,247,0.14)] hover:bg-[rgba(168,85,247,0.18)]' : 'hover:bg-[rgba(168,85,247,0.08)]') : (isSelected ? 'bg-blue-50 hover:bg-gray-50' : 'hover:bg-gray-50')}`}
                    onClick={() => handleToggleOption(optionValue)}
                  >
                    <div className="flex items-center">
                      {showSerialNumber && isSelected && (
                        <span className="inline-flex items-center justify-center bg-blue-100 text-blue-800 font-bold rounded-full w-4 h-4 mr-2 text-[10px] shrink-0">
                          {selectedValues.indexOf(optionValue) + 1}
                        </span>
                      )}
                      <span className={`text-sm ${dark ? (isSelected ? 'text-white font-medium' : 'text-[#e6e9f6]') : (isSelected ? 'text-blue-600 font-medium' : 'text-gray-700')}`}>
                        {optionLabel}
                      </span>
                    </div>
                    {isSelected && (
                      <Check className={`h-4 w-4 shrink-0 ${dark ? 'text-[#c084fc]' : 'text-blue-600'}`} />
                    )}
                  </div>
                );
              })
            )}
          </div>
          
          {/* Footer with actions */}
          {selectedValues.length > 0 && (
            <div className={`border-t px-3 py-2 ${dark ? 'border-[rgba(150,165,210,0.14)] bg-[rgba(7,10,28,0.6)]' : 'border-gray-200 bg-gray-50'}`}>
              <button
                onClick={handleClearAll}
                className={`text-xs ${dark ? 'text-rose-400 hover:text-rose-300' : 'text-red-600 hover:text-red-800'}`}
              >
                Clear all ({selectedValues.length})
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;